import * as React from 'react';
import { addMinutes, utcDate, owaDate, OwaDate, getTimestamp } from 'owa-datetime';
import { DateRange } from 'owa-datetime-utils';
import { ItemsLayout } from '../interfaces/ItemsLayout';
import { SurfaceStore } from 'owa-calendar-surface-core';
import { observer } from 'mobx-react';
import toPercentString from '../utils/toPercentString';

/** Interface implemented by GridLine/GridLineHeader */
export interface GridLineItemProps<TClassNames> {
    /** CSS class names to be applied on the item. */
    classNames?: TClassNames;
    /** Additional CSS properties to be applied on this item */
    style?: React.CSSProperties;
    /** The time of the day this grid line item represents */
    time: OwaDate;
    /** The range of dates displayed in the grid */
    dateRange: DateRange;
    /** If the hour is the first hour a day */
    showIsNewDay?: boolean;
}

/** Renders a set of grid line items as a fragment */
export default observer(function GridLinesFragment<TClassNames>({
    intervalInMinutes,
    itemClassNames,
    itemsLayout,
    itemType: GridLineItem,
    showIsNewDay,
    store,
    timeZoneId,
}: {
    intervalInMinutes: number;
    itemClassNames?: TClassNames;
    itemsLayout: ItemsLayout;
    itemType: React.ComponentType<GridLineItemProps<TClassNames>>;
    showIsNewDay: boolean;
    store: SurfaceStore;
    timeZoneId?: string;
}) {
    let result: JSX.Element[] = [];

    // Account for the minute in which the column starts and its length.
    const toMinutes = store.dayStartMinute + store.dayLength;

    const startTime = store.dayStartMinute;
    const endTime = toMinutes;

    // By default grid lines won't show daylight savings changes, so use UTC.
    // If a time zone is specified, then we're showing multiple columns in which
    // case the base date will be the grid's starting date, in this time zone.
    // In that case, daylight saving transitions show up.
    const baseDate = timeZoneId
        ? owaDate(timeZoneId, store.dateRange.start)
        : utcDate(0, 0, 0, 0, 0);

    // Since we are denoting current time across the grid line, we do not want
    // the text for the first hour of the day, since this would get cut by the header.
    // NOTE: this is very OWA specific, so it shouldn't be in this function. Oh well...
    for (
        let minuteOfDay = startTime + intervalInMinutes;
        minuteOfDay < endTime;
        minuteOfDay += intervalInMinutes
    ) {
        const time = addMinutes(baseDate, minuteOfDay);

        const start = minuteOfDay - startTime;
        const end = start;

        // Get the layout position of that interval and the corresponding CSS
        const verticalLayout = itemsLayout.getVerticalLayout({ start, end });
        const top = toPercentString(verticalLayout.top);
        const key = getTimestamp(time);

        // Create a grid item
        result.push(
            <GridLineItem
                key={key}
                classNames={itemClassNames}
                style={{ top }}
                time={time}
                dateRange={store.dateRange}
                showIsNewDay={showIsNewDay}
            />
        );
    }

    return <>{result}</>;
});
